// Right-side drill panel for a selected Pipeline node (spec §5, D3 + D5 + D7).
//
// Resolves the node's live resources from the snapshot (paint-by-name) and shows:
//   - larger 24h charts (invocations / errors / duration, or queue depth),
//   - resource config (runtime / memory / timeout / lastModified),
//   - a CloudWatch log tail (GET /logs/cloudwatch) + "Open full logs ↗",
//   - controls relocated from the old Pipeline.tsx (D5): rule enable/disable
//     (POST /pipeline/rule) and DLQ retry (POST /pipeline/dlq-retry),
//   - for jobSource nodes (D7): the "Recent runs" transactions panel.
//
// SECURITY: log/job strings render as React children → auto-escaped. No
// dangerouslySetInnerHTML anywhere.
import { useCallback, useEffect, useMemo, useState } from "react";
import { invokeOps, errMsg } from "../../lib/adminApi";
import type { PipelineNode } from "../../lib/pipelineTopology";
import type {
  PipelineSnapshot,
  LambdaInfo,
  QueueInfo,
  RuleInfo,
  LogEvent,
} from "../../lib/types";
import { Banner, Badge } from "../ui";
import { Sparkline } from "./Sparkline";
import JobRunsPanel from "./JobRunsPanel";

// Resolve the live snapshot resources this node paints from (§3 paint-by-name).
function resolve(node: PipelineNode, snap: PipelineSnapshot | null) {
  const m = node.match ?? {};
  const lambda: LambdaInfo | undefined = m.lambda
    ? snap?.lambdas.find((l) => l.name === m.lambda)
    : undefined;
  const queue: QueueInfo | undefined = m.queue
    ? snap?.queues.find((q) => q.name === m.queue)
    : undefined;
  const dlq: QueueInfo | undefined = m.dlq
    ? snap?.queues.find((q) => q.name === m.dlq)
    : undefined;
  const rule: RuleInfo | undefined = m.rule
    ? snap?.rules.find((r) => r.name === m.rule)
    : undefined;
  return { lambda, queue, dlq, rule };
}

function Chart({ title, values, color }: { title: string; values: number[]; color: string }) {
  const last = values.length ? values[values.length - 1] : null;
  const peak = values.length ? Math.max(...values) : null;
  return (
    <div className="rounded border border-zinc-800 bg-zinc-950 p-2">
      <div className="mb-1 flex items-center justify-between text-[10px] uppercase tracking-wide text-zinc-500">
        <span>{title}</span>
        <span className="text-zinc-400">
          {last != null ? `now ${last}` : "no series"}
          {peak != null ? ` · peak ${peak}` : ""}
        </span>
      </div>
      <Sparkline values={values} color={color} width={240} height={40} className="w-full" />
    </div>
  );
}

export default function NodeDrawer({
  node,
  snapshot,
  onClose,
  onChanged,
}: {
  node: PipelineNode;
  snapshot: PipelineSnapshot | null;
  onClose: () => void;
  onChanged: () => void; // ask the page to refresh the snapshot after a control action
}) {
  // Memoized so `rule` etc. are stable references between renders — the
  // useCallback controls below depend on them.
  const { lambda, queue, dlq, rule } = useMemo(() => resolve(node, snapshot), [node, snapshot]);
  const fnName = lambda?.name ?? node.match?.lambda ?? null;

  // --- CloudWatch log tail ---
  const [logs, setLogs] = useState<LogEvent[] | null>(null);
  const [logErr, setLogErr] = useState<string | null>(null);
  const [logLoading, setLogLoading] = useState(false);

  useEffect(() => {
    if (!fnName) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- clear the log tail when the selected node has no Lambda
      setLogs(null);
      return;
    }
    let cancelled = false;
    setLogLoading(true);
    setLogErr(null);
    const group = `/aws/lambda/${fnName}`;
    const params = new URLSearchParams({ logGroup: group, limit: "20" });
    invokeOps<{ events: LogEvent[] }>(`/logs/cloudwatch?${params}`)
      .then((d) => {
        if (!cancelled) setLogs(d.events);
      })
      .catch((e) => {
        if (!cancelled) setLogErr(errMsg(e));
      })
      .finally(() => {
        if (!cancelled) setLogLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [fnName]);

  // --- Controls (relocated from Pipeline.tsx, D5) ---
  const [busy, setBusy] = useState(false);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [actionErr, setActionErr] = useState<string | null>(null);

  const toggleRule = useCallback(async () => {
    if (!rule) return;
    setBusy(true);
    setActionErr(null);
    setActionMsg(null);
    try {
      await invokeOps("/pipeline/rule", {
        method: "POST",
        body: JSON.stringify({ name: rule.name, enabled: rule.state !== "ENABLED" }),
      });
      setActionMsg(rule.state === "ENABLED" ? "Disabled" : "Enabled");
      onChanged();
    } catch (e) {
      setActionErr(errMsg(e));
    } finally {
      setBusy(false);
    }
  }, [rule, onChanged]);

  const retryDlq = useCallback(
    async (dlqUrl: string) => {
      setBusy(true);
      setActionErr(null);
      setActionMsg(null);
      try {
        // Only the DLQ URL is sent — the ops Lambda derives the paired source
        // queue server-side (*_dead → *_queue), matching the old Pipeline.tsx.
        const result = await invokeOps<{ moved: number }>("/pipeline/dlq-retry", {
          method: "POST",
          body: JSON.stringify({ dlqUrl }),
        });
        setActionMsg(`Moved ${result.moved} message${result.moved === 1 ? "" : "s"}`);
        onChanged();
      } catch (e) {
        setActionErr(errMsg(e));
      } finally {
        setBusy(false);
      }
    },
    [onChanged],
  );

  const lm = lambda?.metrics;

  return (
    <aside className="flex h-full w-[22rem] flex-col overflow-y-auto border-l border-zinc-800 bg-zinc-900 p-3 text-sm">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="font-semibold text-zinc-100">{node.label}</h3>
          <p className="text-[11px] uppercase tracking-wide text-zinc-500">{node.kind}</p>
          {/* description (moved off the trimmed card face) */}
          {node.sublabel && <p className="mt-0.5 text-[11px] text-zinc-400">{node.sublabel}</p>}
          {fnName && (
            <p className="mt-0.5 font-mono text-[10px] text-zinc-500">{fnName}</p>
          )}
        </div>
        <button
          onClick={onClose}
          className="rounded border border-zinc-700 px-2 py-0.5 text-xs text-zinc-400 hover:bg-zinc-800"
          aria-label="Close"
        >
          ✕
        </button>
      </div>

      {/* ===== Lambda / service charts + config ===== */}
      {(node.kind === "processor" || node.kind === "producer" || node.kind === "service") && (
        <section className="mt-3 space-y-2">
          <Chart title="Invocations (24h)" values={lm?.series?.invocations ?? []} color="#34d399" />
          <Chart title="Errors (24h)" values={lm?.series?.errors ?? []} color="#f87171" />
          <Chart title="Duration ms (24h)" values={lm?.series?.duration ?? []} color="#60a5fa" />
          {lm && (
            <div className="grid grid-cols-3 gap-2 text-center text-xs">
              <div className="rounded border border-zinc-800 bg-zinc-950 p-1.5">
                <div className="text-zinc-100">{lm.invocations}</div>
                <div className="text-[10px] text-zinc-500">inv/24h</div>
              </div>
              <div className="rounded border border-zinc-800 bg-zinc-950 p-1.5">
                <div className={lm.errors > 0 ? "text-red-400" : "text-zinc-100"}>{lm.errors}</div>
                <div className="text-[10px] text-zinc-500">errors</div>
              </div>
              <div className="rounded border border-zinc-800 bg-zinc-950 p-1.5">
                <div className="text-zinc-100">{Math.round(lm.duration)}</div>
                <div className="text-[10px] text-zinc-500">avg ms</div>
              </div>
            </div>
          )}

          {/* config */}
          <dl className="grid grid-cols-2 gap-x-2 gap-y-1 rounded border border-zinc-800 bg-zinc-950 p-2 text-[11px]">
            <dt className="text-zinc-500">runtime</dt>
            <dd className="text-zinc-300">{lambda?.runtime ?? "—"}</dd>
            <dt className="text-zinc-500">memory</dt>
            <dd className="text-zinc-300">{lambda?.memory != null ? `${lambda.memory} MB` : "—"}</dd>
            <dt className="text-zinc-500">timeout</dt>
            <dd className="text-zinc-300">{lambda?.timeout != null ? `${lambda.timeout}s` : "—"}</dd>
            <dt className="text-zinc-500">modified</dt>
            <dd className="text-zinc-300">{lambda?.lastModified?.slice(0, 10) ?? "—"}</dd>
          </dl>

          {/* collapsed children expanded */}
          {node.collapsedChildren && (
            <div className="rounded border border-zinc-800 bg-zinc-950 p-2 text-[11px]">
              <div className="mb-1 text-zinc-400">{node.collapsedChildren.label}</div>
              <ul className="space-y-0.5 font-mono text-[10px] text-zinc-500">
                {node.collapsedChildren.lambdas.map((l) => (
                  <li key={l}>{l}</li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}

      {/* ===== Queue charts ===== */}
      {(node.kind === "queue" || node.kind === "dlq") && (
        <section className="mt-3 space-y-2">
          {(() => {
            const q = node.kind === "dlq" ? dlq : queue;
            return (
              <>
                <div className="grid grid-cols-2 gap-2 text-center text-xs">
                  <div className="rounded border border-zinc-800 bg-zinc-950 p-2">
                    <div className={q?.isDlq && (q?.messages ?? 0) > 0 ? "text-red-400" : "text-zinc-100"}>
                      {q?.messages ?? "—"}
                    </div>
                    <div className="text-[10px] text-zinc-500">depth</div>
                  </div>
                  <div className="rounded border border-zinc-800 bg-zinc-950 p-2">
                    <div className="text-zinc-100">{q?.inFlight ?? "—"}</div>
                    <div className="text-[10px] text-zinc-500">in-flight</div>
                  </div>
                </div>
                <Chart title="Depth (24h)" values={q?.metrics?.depthSeries ?? []} color="#60a5fa" />
              </>
            );
          })()}
        </section>
      )}

      {/* ===== Controls (D5) ===== */}
      {(rule || dlq) && (
        <section className="mt-3 space-y-2">
          <h4 className="text-[11px] uppercase tracking-wide text-zinc-500">Controls</h4>
          {rule && (
            <div className="flex items-center gap-2">
              <Badge ok={rule.state === "ENABLED"}>{rule.state}</Badge>
              <button
                onClick={toggleRule}
                disabled={busy}
                className="rounded border border-zinc-700 px-2 py-0.5 text-xs hover:bg-zinc-800 disabled:opacity-50"
              >
                {busy ? "…" : rule.state === "ENABLED" ? "Disable" : "Enable"}
              </button>
            </div>
          )}
          {dlq && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-zinc-400">DLQ: {dlq.messages} msg</span>
              <button
                onClick={() => retryDlq(dlq.url)}
                disabled={busy || dlq.messages === 0}
                className="rounded border border-zinc-700 px-2 py-0.5 text-xs hover:bg-zinc-800 disabled:opacity-50"
              >
                {busy ? "…" : "Retry"}
              </button>
            </div>
          )}
          {actionMsg && <p className="text-xs text-emerald-400">{actionMsg}</p>}
          {actionErr && <Banner className="mt-1">{actionErr}</Banner>}
        </section>
      )}

      {/* ===== Logs tail ===== */}
      {fnName && (
        <section className="mt-3">
          <div className="mb-1 flex items-center justify-between">
            <h4 className="text-[11px] uppercase tracking-wide text-zinc-500">Recent logs</h4>
            <a
              href={`/logs?group=/aws/lambda/${fnName}`}
              target="_blank"
              rel="noreferrer"
              className="text-[11px] text-sky-400 hover:underline"
            >
              Open full logs ↗
            </a>
          </div>
          {logLoading && <p className="text-xs text-zinc-500">Loading logs…</p>}
          {logErr && <Banner className="mt-1">{logErr}</Banner>}
          {logs && logs.length > 0 && (
            <div className="max-h-48 overflow-y-auto rounded border border-zinc-800 bg-zinc-950 divide-y divide-zinc-800">
              {logs.map((ev, i) => (
                <pre
                  key={i}
                  className="whitespace-pre-wrap break-all px-2 py-1 font-mono text-[10px] text-zinc-400"
                >
                  {ev.message}
                </pre>
              ))}
            </div>
          )}
          {logs && logs.length === 0 && !logLoading && (
            <p className="text-xs text-zinc-500">No recent log events.</p>
          )}
        </section>
      )}

      {/* ===== Recent runs (jobSource nodes only, D7) ===== */}
      {node.jobSource && (
        <section className="mt-3">
          <h4 className="mb-1 text-[11px] uppercase tracking-wide text-zinc-500">
            Recent runs (transactions)
          </h4>
          <JobRunsPanel table={node.jobSource.table} type={node.jobSource.type} />
        </section>
      )}
    </aside>
  );
}
